/**
 * Edit Bridge Session Objective modal: load session, edit objective/valuations/revenue/expense/months, save via updateBridgeSession.
 */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, message } from 'antd';
import { getBridgeSession, updateBridgeSession, type UpdateBridgeSessionPayload } from '../../services/localBridgeSessionApi';
import { BridgeSessionObjectiveForm, type BridgeSessionObjectiveFormValues } from './BridgeSessionObjectiveForm';

export interface EditBridgeSessionObjectiveModalProps {
  sessionId: string;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const EditBridgeSessionObjectiveModal: React.FC<EditBridgeSessionObjectiveModalProps> = ({
  sessionId,
  open,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm<BridgeSessionObjectiveFormValues>();
  const [loading, setLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoadingSession(true);
    getBridgeSession(sessionId)
      .then((session) => {
        if (session) {
          form.setFieldsValue({
            objective: session.bridge_objective ?? undefined,
            current_valuation: session.current_valuation ?? undefined,
            future_valuation: session.target_valuation ?? undefined,
            current_revenue: session.current_revenue ?? undefined,
            current_expense: session.current_expense ?? undefined,
            estimated_months: session.target_months ?? undefined,
            cost_one_hour_value: session.commitment_toll_cost_1hr ?? undefined,
          });
        }
      })
      .finally(() => setLoadingSession(false));
  }, [open, sessionId, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload: UpdateBridgeSessionPayload = {
        bridge_objective: values.objective?.trim() || undefined,
        current_valuation: values.current_valuation,
        target_valuation: values.future_valuation,
        target_months: values.estimated_months,
        current_revenue: values.current_revenue,
        current_expense: values.current_expense,
        commitment_toll_cost_1hr: values.cost_one_hour_value ?? undefined,
      };
      const updated = await updateBridgeSession(sessionId, payload);
      if (updated) {
        message.success('Objective updated');
        onSaved?.();
        onClose();
      } else {
        message.error('Failed to update');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please fill required fields');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Bridge Session Objective"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Save
        </Button>,
      ]}
      width={560}
      destroyOnClose
    >
      {loadingSession ? (
        <div style={{ padding: 24, textAlign: 'center' }}>Loading session...</div>
      ) : (
        <Form form={form} layout="vertical">
          <BridgeSessionObjectiveForm
            form={form}
            showDomain={false}
            showCommitmentToll
            showEstimate
          />
        </Form>
      )}
    </Modal>
  );
};

export default EditBridgeSessionObjectiveModal;
